import { useState, useEffect, useRef, useCallback } from 'react';
import { Form } from 'antd';
import { FileData, ImagePayload, ProductData, PurchaseOrderStatusCode, QueryParams } from 'types';
import { InventoryDocument, InventoryDocumentItem, PaymentType } from 'models';
import {
  useCreatePurchaseOrderMutation,
  useGetPurchaseOrderByIdQuery,
  useUpdatePurchaseOrderMutation,
  useGenerateCodeMutation,
  useGetStatusQuery,
  useCreateAttachmentMutation,
  useGetAllAttachmentTypesQuery,
  useGetAllTransactionMethodsQuery,
  useLazyGetTransactionsByRefIdQuery,
  useSingleUploadMutation,
} from 'services';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { showAlertNotification } from 'components/common';
import { generateRandomBarcode } from 'utils';
import { Transaction } from 'models/Transaction';
import { TransactionMethodCode } from 'models/TransactionMethod';
import dayjs, { Dayjs } from 'dayjs';
export interface UseImportOrderOperationsProps {
  purchaseOrderId: string;
  stepDone?: boolean;
  isModalView?: boolean;
}

export const useImportOrderOperations = ({
  purchaseOrderId,
  stepDone = false,
  isModalView = false,
}: UseImportOrderOperationsProps) => {
  // State
  const [checkValueEmpty, setCheckValueEmpty] = useState<boolean>(false);
  const onPrevStepRef = useRef<() => void | undefined>(undefined);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentMethod, setPaymentMethod] = useState<PaymentType>(PaymentType.PARTIAL_PAYMENT_FLEX);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [transactionMethod, setTransactionMethod] = useState<TransactionMethodCode>('CASH');
  const [datePayment, setDatePayment] = useState<Dayjs | undefined>(undefined);
  // Create state for the current purchase order
  const [currentPurchaseOrder, setCurrentPurchaseOrder] = useState<InventoryDocument>({
    id: '',
    code: '',
    deliveryDate: new Date(),
    supplierId: '',
    warehouseId: '',
    userName: '',
    userPhoneNumber: '',
    statusId: '',
    total: 0,
    paidMoney: 0,
    unpaidMoney: 0,
    items: [],
    inventoryDocumentItems: [],
  });
  const location = useLocation();
  const selectedItems = location.state?.selectedItems as InventoryDocumentItem[] | undefined;
  const [selectedProduct, setSelectedProduct] = useState<ProductData | undefined>(undefined);
  const [originalPurchaseOrder, setOriginalPurchaseOrder] = useState<InventoryDocument | undefined>(undefined);
  // Fetch purchase order data from API
  const [createPurchaseOrder, { isLoading: isCreating }] = useCreatePurchaseOrderMutation();
  const [updatePurchaseOrder, { isLoading: isUpdating }] = useUpdatePurchaseOrderMutation();
  const [createAttachment] = useCreateAttachmentMutation();
  const [transactionAttachments, setTransactionAttachments] = useState<FileData[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filesData, setFilesData] = useState<ImagePayload[]>([]);
  const [singleUpload] = useSingleUploadMutation();
  const [getTransactionByRefId] = useLazyGetTransactionsByRefIdQuery();

  const [generateCode] = useGenerateCodeMutation();
  const { data: statusData } = useGetStatusQuery(
    {
      query: {
        filter: `type:eq:${'purchase-order'}`,
      },
    },
    {
      skip: isModalView,
    },
  );
  const { data: purchaseOrderData, isLoading } = useGetPurchaseOrderByIdQuery({
    id: purchaseOrderId,
    query: {
      includeRef: 'Debt',
      include:
        'status|warehouse|supplier.baseUser|createdBy.baseUser|inventoryDocumentItems.productVersion.baseUnit|inventoryDocumentItems.productVersion.currencyUnit|inventoryDocumentItems.productVersion.category|inventoryDocument|inventoryDocumentAttachments.attachment',
    } as QueryParams,
  });
  const { data: attachmentTypes } = useGetAllAttachmentTypesQuery(
    {
      query: {
        size: 'unlimited',
      },
    },
    { skip: !purchaseOrderId },
  );
  const { data: transactionMethodsData } = useGetAllTransactionMethodsQuery(
    {
      query: {
        size: 'unlimited',
      },
    },
    {
      skip: !purchaseOrderId || isModalView,
    },
  );
  // Update purchase order when data changes
  useEffect(() => {
    if (purchaseOrderData) {
      if (purchaseOrderData.data.id) {
        getTransactionByRefId({
          refId: purchaseOrderData.data.id,
          query: {
            size: 'unlimited',
          },
        }).then((result) => {
          const transactionData = result.data?.data.rows || [];
          if (transactionData) {
            setTransactions(transactionData);
          }
        });
      }
      setCurrentPurchaseOrder(purchaseOrderData.data);
      setFilesData(
        purchaseOrderData.data.inventoryDocumentAttachments?.map((attachment) => ({
          id: attachment.attachmentId,
          filePath: attachment.attachment?.filePath || '',
          name: attachment.attachment?.name || '',
          order: 0,
          url: attachment.attachment?.filePath || '',
        })) || [],
      );
      setOriginalPurchaseOrder(purchaseOrderData.data);
      if (!isModalView) {
        if (purchaseOrderData.data.status?.code === PurchaseOrderStatusCode.RECEIVED && !stepDone) {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('stepDone', 'true');
            return newParams;
          });
        }
        if (purchaseOrderData.data.status?.code !== PurchaseOrderStatusCode.RECEIVED) {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('stepDone');
            return newParams;
          });
        }
      }
    }
    form.setFieldsValue({
      code: purchaseOrderData?.data?.code,
      userName: purchaseOrderData?.data?.createdBy?.baseUser?.name,
      email: purchaseOrderData?.data?.createdBy?.baseUser?.email,
      phoneNumber: purchaseOrderData?.data?.createdBy?.baseUser?.phoneNumber,
    });
  }, [purchaseOrderData, form, getTransactionByRefId, setSearchParams]);
  useEffect(() => {
    if (selectedItems && selectedItems.length > 0 && currentPurchaseOrder?.id) {
      const currentItemIds = (currentPurchaseOrder.inventoryDocumentItems || [])
        .map((item) => item.id)
        .sort()
        .join(',');
      const selectedItemIds = selectedItems
        .map((item) => item.id)
        .sort()
        .join(',');
      if (currentItemIds !== selectedItemIds) {
        const newTotal = selectedItems.reduce((sum, item) => {
          return sum + (item.costPrice || 0) * (item.actualQuantity || item.quantity || 0);
        }, 0);
        setCurrentPurchaseOrder((prev) => ({
          ...prev,
          inventoryDocumentItems: selectedItems,
          total: newTotal * ((prev.vat || 0) / 100 + 1),
          unpaidMoney: (newTotal || 0) - (prev.paidMoney || 0),
        }));
      }
    }
  }, [selectedItems, currentPurchaseOrder?.id]);
  // Event handlers
  const handleCancelWithConfirm = useCallback(() => {
    setCheckValueEmpty(true);
  }, []);
  const handleCreateNewOrder = async () => {
    const isPartial = location.state?.isPartial;
    if (isPartial) {
      const allOriginalItems = originalPurchaseOrder?.inventoryDocumentItems || [];
      const selectedItemIds = (currentPurchaseOrder.inventoryDocumentItems || []).map((item) => item.id);
      const paidMoney =
        currentPurchaseOrder?.inventoryDocumentItems?.reduce((sum, item) => {
          return sum + (item.costPrice || 0) * (item.actualQuantity || item.quantity || 0);
        }, 0) || 0;
      const remainingItems = allOriginalItems
        .filter((item) => !selectedItemIds.includes(item.id))
        .map((item) => ({
          ...item,
          total: Number(item.total || 0),
          costPrice: Number(item.costPrice || 0),
        }));
      if (remainingItems.length > 0) {
        const generatedCode = await generateCode({ code: 'PO' }).unwrap();
        const randomBarcode = generateRandomBarcode();
        const newOrderData = {
          ...originalPurchaseOrder,
          barcode: randomBarcode,
          supplierId: originalPurchaseOrder?.supplierId || '',
          warehouseId: originalPurchaseOrder?.warehouseId || '',
          userName: originalPurchaseOrder?.userName || '',
          userPhoneNumber: originalPurchaseOrder?.userPhoneNumber || '',
          description: originalPurchaseOrder?.description || '',
          note: originalPurchaseOrder?.note || '',
          type: originalPurchaseOrder?.type,
          deliveryDate: originalPurchaseOrder?.deliveryDate || new Date(),
          paymentType: originalPurchaseOrder?.paymentType || PaymentType.FULL_PAYMENT,
          payInAdvance: Number(
            (originalPurchaseOrder?.payInAdvance || 0) - paidMoney > 0
              ? Number((originalPurchaseOrder?.payInAdvance || 0) - paidMoney)
              : 0,
          ),
          code: generatedCode,
          statusId: originalPurchaseOrder?.statusId || '',
          paidMoney: 0,

          total: remainingItems.reduce((sum, item) => sum + (item.total || 0), 0),
          inventoryDocumentItems: remainingItems.map((item) => ({
            productVersionId: item.productVersionId,
            quantity: item.quantity,
            costPrice: item.costPrice,
            total: item.total,
          })),
        };

        await createPurchaseOrder(newOrderData as unknown as InventoryDocument).unwrap();
        showAlertNotification({
          type: 'success',
          title: 'Tạo đơn nhập hàng thành công',
          message: 'Đơn nhập hàng mới đã được tạo thành công với các sản phẩm còn lại.',
        });
      }
    }
  };
  const handleSaveChanges = useCallback(async () => {
    if (!currentPurchaseOrder?.id) {
      return;
    }

    try {
      // Prepare data for updating the purchase order
      const updateData = {
        id: currentPurchaseOrder.id,
        type: currentPurchaseOrder.type,
        statusId: statusData?.data?.filter((status) => status.code === PurchaseOrderStatusCode.INPROGRESS)[0]?.id || '',
        inventoryDocumentItems: currentPurchaseOrder.inventoryDocumentItems?.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          actualQuantity: item.actualQuantity || item.quantity,
          productVersionId: item.productVersionId,
        })),
      } as Partial<InventoryDocument> & { id: string };

      await updatePurchaseOrder(updateData).unwrap();

      // if isPartial is true, create a new purchase order with remaining items
      handleCreateNewOrder();

      showAlertNotification({
        type: 'success',
        title: 'Lưu đơn nhập hàng thành công',
        message: 'Đơn nhập hàng đã được cập nhật thành công.',
      });
      navigate('/management/inventory/purchase-orders');
    } catch (error) {
      console.error('Lỗi khi cập nhật đơn hàng:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi cập nhật đơn hàng',
        message: 'Đã xảy ra lỗi khi cập nhật đơn nhập hàng. Vui lòng thử lại sau.',
      });
    }
  }, [
    currentPurchaseOrder,
    originalPurchaseOrder,
    updatePurchaseOrder,
    createPurchaseOrder,
    location.state,
    handleCreateNewOrder,
  ]);
  const handleCancelDirect = useCallback(() => {
    navigate('/management/inventory/purchase-orders');
  }, [navigate]);

  const handleConfirmCancel = useCallback(() => {
    setCheckValueEmpty(false);
    navigate('/management/inventory/purchase-orders');
  }, [navigate]);

  const handleSelectProduct = (product: ProductData) => {
    setSelectedProduct(product);
  };

  const handleSaveActualQuantity = (productId: string, actualQuantity: number | null) => {
    const currentItems = selectedItems || currentPurchaseOrder?.inventoryDocumentItems || [];
    // Update the actual quantity and total for the selected product
    const updatedItems = currentItems.map((item) => {
      if (item.id === productId) {
        if (actualQuantity === null) {
          const newItem = { ...item };
          delete newItem.actualQuantity;
          return newItem;
        }

        const newTotal = (item.costPrice || 0) * actualQuantity;
        return {
          ...item,
          actualQuantity,
          total: newTotal as number,
        };
      }
      return item;
    });

    const newTotal = updatedItems.reduce((sum, item) => {
      return sum + (item.costPrice || 0) * (item.actualQuantity || item.quantity || 0);
    }, 0);
    // if have selectedItems, update currentPurchaseOrder and navigate

    if (selectedItems) {
      setCurrentPurchaseOrder({
        ...currentPurchaseOrder,
        inventoryDocumentItems: updatedItems,
        total: newTotal * (1 + (currentPurchaseOrder.vat || 0) / 100) || 0,
        unpaidMoney: (newTotal * (1 + (currentPurchaseOrder.vat || 0) / 100) || 0) - currentPurchaseOrder.paidMoney,
      });

      navigate(location.pathname, {
        state: {
          ...location.state,
          selectedItems: updatedItems,
        },
        replace: true,
      });
    } else {
      setCurrentPurchaseOrder({
        ...currentPurchaseOrder,
        inventoryDocumentItems: updatedItems,
        total: newTotal * (1 + (currentPurchaseOrder.vat || 0) / 100) || 0,
        unpaidMoney: (newTotal * (1 + (currentPurchaseOrder.vat || 0) / 100) || 0) - currentPurchaseOrder.paidMoney,
      });
    }

    setSelectedProduct(undefined);
  };
  const handleImport = useCallback(async () => {
    if (!currentPurchaseOrder?.id) {
      return;
    }

    try {
      // Prepare data for updating the purchase order
      const updateData = {
        id: currentPurchaseOrder.id,
        type: currentPurchaseOrder.type,
        statusId: statusData?.data?.filter((status) => status.code === PurchaseOrderStatusCode.RECEIVED)[0]?.id || '',
        inventoryDocumentItems: currentPurchaseOrder.inventoryDocumentItems?.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          actualQuantity: item?.actualQuantity || item.quantity,
          productVersionId: item.productVersionId,
        })),
      } as Partial<InventoryDocument> & { id: string };

      await updatePurchaseOrder(updateData).unwrap();

      handleCreateNewOrder();
      showAlertNotification({
        type: 'success',
        title: 'Nhập hàng thành công',
        message: 'Đơn đã được nhập hàng thành công.',
      });
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('stepDone', 'true');
        return newParams;
      });
    } catch (error) {
      console.error('Lỗi khi nhập hàng:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi nhập hàng',
        message: 'Đã xảy ra lỗi khi nhập hàng. Vui lòng thử lại sau.',
      });
    }
  }, [currentPurchaseOrder, originalPurchaseOrder, updatePurchaseOrder, createPurchaseOrder, location.state]);
  const handleUpload = useCallback(
    async (files: File[]): Promise<void> => {
      try {
        for (const file of files) {
          const attachment = await createAttachment({
            name: 'Transaction Attachment ' + new Date().toISOString(),
            filePath: file.name,
            attachmentTypeId: attachmentTypes?.data?.rows.filter((type) => type.code === 'transaction')[0]?.id || '',
          }).unwrap();
          setTransactionAttachments((prev) => [
            ...prev,
            {
              id: attachment.data.id,
              filePath: attachment.data.filePath,
              order: prev.length + 1,
              name: file.name,
            },
          ]);
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi tải lên tệp',
          message: 'Đã xảy ra lỗi khi tải lên tệp. Vui lòng thử lại.',
        });
      }
    },
    [createAttachment, attachmentTypes, transactionAttachments],
  );

  const handlePaymentSubmit = useCallback(async () => {
    if (!currentPurchaseOrder?.id) {
      return;
    }
    let transaction = null;
    if (paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX) {
      if (Number(customAmount) < 0) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi thanh toán',
          message: 'Số tiền thanh toán không hợp lệ.',
        });
        return;
      }
      if (Number(customAmount) > currentPurchaseOrder.total - (currentPurchaseOrder?.payInAdvance || 0)) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi thanh toán',
          message: 'Số tiền thanh toán phải nhỏ hơn số tiền còn lại.',
        });
        return;
      }
      transaction = {
        transactionMethodId: transactionMethodsData?.data.rows[0]?.id,
        transactionAmount: Number(customAmount) || 0,
        transactionAttachments: transactionAttachments.map((attachment) => ({
          attachmentId: attachment.id,
          description: 'payment purchase order attachment',
        })),
      };
    } else if (paymentMethod === PaymentType.FULL_PAYMENT) {
      transaction = {
        transactionMethodId: transactionMethodsData?.data.rows[0]?.id,
        transactionAmount: currentPurchaseOrder.total - (currentPurchaseOrder?.payInAdvance || 0),
        transactionAttachments: transactionAttachments.map((attachment) => ({
          attachmentId: attachment.id,
          description: 'payment purchase order attachment',
        })),
      };
    }

    try {
      // Prepare data for updating the purchase order
      const updateData = {
        id: currentPurchaseOrder.id,
        type: currentPurchaseOrder.type,
        statusId: statusData?.data?.filter((status) => status.code === PurchaseOrderStatusCode.RECEIVED)[0]?.id || '',
        inventoryDocumentItems: currentPurchaseOrder.inventoryDocumentItems?.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          actualQuantity: item?.actualQuantity || item.quantity,
          productVersionId: item.productVersionId,
        })),
        total: currentPurchaseOrder.total,
        debtDeadline:
          paymentMethod !== PaymentType.FULL_PAYMENT && datePayment
            ? dayjs(datePayment).endOf('day').toISOString()
            : undefined,
        payInAdvance: Number(currentPurchaseOrder.payInAdvance || 0) + Number(transaction?.transactionAmount || 0),
        transaction,
        paymentType: paymentMethod,
      } as Partial<InventoryDocument> & { id: string };

      await updatePurchaseOrder(updateData).unwrap();

      handleCreateNewOrder();
      showAlertNotification({
        type: 'success',
        title: 'Nhập hàng thành công',
        message: 'Đơn đã được nhập hàng thành công.',
      });
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('stepDone', 'true');
        return newParams;
      });
    } catch (error) {
      console.error('Lỗi khi nhập hàng:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi nhập hàng',
        message: 'Đã xảy ra lỗi khi nhập hàng. Vui lòng thử lại sau.',
      });
    }
  }, [
    paymentMethod,
    customAmount,
    currentPurchaseOrder,
    originalPurchaseOrder,
    updatePurchaseOrder,
    createPurchaseOrder,
    location.state,
    statusData,
    transactionAttachments,
    transactionMethodsData,
  ]);
  const handleOnNewFileUpload = useCallback(
    async (filePath: string, fileName: string) => {
      // create attachment
      const attachment = await createAttachment({
        name: fileName,
        filePath,
        attachmentTypeId: attachmentTypes?.data?.rows[0]?.id || '',
      }).unwrap();
      if (purchaseOrderId) {
        try {
          await singleUpload({
            attachmentId: attachment.data.id,
            attachmentType: 'InventoryDocumentAttachment',
            entityName: 'InventoryDocument',
            entityId: purchaseOrderId,
          }).unwrap();
          // clearCart();
          showAlertNotification({
            type: 'success',
            title: 'Tải lên chứng từ thành công',
            message: 'Chứng từ đã được tải lên thành công.',
          });
        } catch (error) {
          console.error('Error uploading single file:', error);
          showAlertNotification({
            type: 'error',
            title: 'Lỗi khi tải lên chứng từ',
            message: 'Đã xảy ra lỗi khi tải lên chứng từ, vui lòng thử lại.',
          });
        }
      }
    },
    [createAttachment, attachmentTypes],
  );
  return {
    // State
    checkValueEmpty,
    setCheckValueEmpty,
    form,
    currentPurchaseOrder,
    selectedProduct,
    isLoading,
    stepDone,
    onPrevStepRef,
    paymentMethod,
    filesData,
    setFilesData,
    transactions,
    setPaymentMethod,
    transactionMethod,
    setTransactionMethod,
    customAmount,
    setCustomAmount,
    searchParams,
    datePayment,
    setDatePayment,
    isUpdating: isUpdating || isCreating,
    // Handlers
    handleSaveChanges,
    handleConfirmCancel,
    handleCancelWithConfirm,
    handleCancelDirect,
    handleSelectProduct,
    handleSaveActualQuantity,
    handleImport,
    handlePaymentSubmit,
    handleUpload,
    handleOnNewFileUpload,
  };
};
